import React, { Fragment, Suspense, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import './App.css';
import './styles/theme.css';
import './styles/tool-light-mode.css';
import './components/CookieConsent.css';
import VisaChart from './components/VisaChart';
import SearchBar from './components/SearchBar';
import CheckoutModal from './components/CheckoutModal';
import GlobalBrowserAlertPrompt from './components/GlobalBrowserAlertPrompt';
import ExitIntentPopup from './components/ExitIntentPopup';
import ActivityFeed from './components/ActivityFeed';
import ProcessingTimeEstimator from './components/ProcessingTimeEstimator';
import HowItWorks from './components/HowItWorks';
import StatisticsTrustBar from './components/StatisticsTrustBar';
import PricingComparison from './components/PricingComparison';
import DecisionCalculator from './components/DecisionCalculator';
import EnhancedFAQ from './components/EnhancedFAQ';
import SEOContent from './components/SEOContent';
import SocialShareBar from './components/SocialShareBar';
import SpreadTheWord from './components/SpreadTheWord';
import EnhancedTestimonials from './components/EnhancedTestimonials';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import matter from 'gray-matter';
import { MDXProvider } from '@mdx-js/react';
import { evaluate } from '@mdx-js/mdx';
import * as runtime from 'react/jsx-runtime';
import {
  TableOfContents, TLDRBox, ComparisonTable, CostCalculator, InteractiveChecklist,
  ProcessFlowchart, Timeline, StatCard, CTABox, ProTip, Warning, FAQSchema,
  VisaStatsEmbed, MetaTags, NotFound, FaqPage, ArticleSchema, Byline,
  PricingPage, PrivacyPage, TermsPage, RefundPage, DataDeletionPage,
  EditorialPolicyPage,
  Breadcrumbs,
  TheorySchema,
  VFSOfficer,
  InterviewCategories,
  FinancialBreakdown,
  CommonMistakesChecklist
} from './components';
import articles, { categoryInfo } from './data/articles';
import useDocumentTheme, { setDocumentTheme } from './hooks/useDocumentTheme';
import useScrollAnimation from './hooks/useScrollAnimation';
import useExitIntent from './hooks/useExitIntent';
import ToolsPage from './pages/Tools';
import CookieConsent from './components/CookieConsent';
import { trackAnalyticsEvent } from './lib/analytics';

// Lazy-load the heavy tool calculators. Each tool is only fetched on
// demand when the user actually navigates to its route, which keeps
// the homepage bundle small and improves LCP.
const LazyEducationLoan = React.lazy(() => import('./components/EducationLoanEMICalculator'));
const LazyCostOfLiving = React.lazy(() => import('./components/CostOfLivingPlanner'));
const LazyUniversityMatcher = React.lazy(() => import('./components/IrelandUniversityMatcher'));
const LazyScholarshipFinder = React.lazy(() => import('./components/IrelandScholarshipFinder'));
const LazyInterviewSimulator = React.lazy(() => import('./components/IrelandVisaInterviewSimulator'));
const LazyStampVisualizer = React.lazy(() => import('./components/IrelandStampVisualizer'));
const LazyTaxCalculator = React.lazy(() => import('./components/IrishSalaryTaxCalculator'));
const LazyAccommodation = React.lazy(() => import('./components/IrelandAccommodationAssistant'));
const LazyDocumentChecklist = React.lazy(() => import('./components/IrelandDocumentChecklist'));
const LazyFeeCalculator = React.lazy(() => import('./components/VisaFeeCalculator'));
const LazyEligibilityQuiz = React.lazy(() => import('./components/VisaEligibilityQuiz'));

const ToolFallback = () => (
  <div className="container section" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
    <p>Loading tool…</p>
  </div>
);
// Simple CountUp without external deps
const CountUp = ({ end = 0, duration = 1200, formatter = (n) => n.toLocaleString() }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now();
    const animate = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setValue(Math.floor(end * p));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return <span>{formatter(value)}</span>;
};

const mdxComponents = {
  TableOfContents,
  TLDRBox,
  ComparisonTable,
  CostCalculator,
  InteractiveChecklist,
  ProcessFlowchart,
  Timeline,
  StatCard,
  CTABox,
  ProTip,
  Warning,
  FAQSchema,
  VisaStatsEmbed,
  TheorySchema,
  VFSOfficer,
  InterviewCategories,
  FinancialBreakdown,
  CommonMistakesChecklist
};

const normalizeRoute = (route) => {
  if (!route) return '/';
  // Ensure leading slash
  const withSlash = route.startsWith('/') ? route : `/${route}`;
  // Remove trailing slash (except root)
  return withSlash !== '/' && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
};

const getRouteFromLocation = () => {
  if (typeof window === 'undefined') return '/';

  const { pathname, search, hash } = window.location;
  let route = `${pathname || '/'}${search || ''}`;
  if (hash && hash.startsWith('#/')) {
    route = hash.substring(1); // remove '#' to match pathname router format
  }
  const normalizedRoute = normalizeRoute(route);
  return normalizedRoute;
};

const HOME_HASH_BLOCKLIST = new Set(['preview', 'hero']);

const sanitizeHomeHashAndScrollTop = () => {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  if (pathname !== '/') return;

  const normalizedHash = (hash || '').replace('#', '').trim().toLowerCase();
  const shouldClearHash = normalizedHash && HOME_HASH_BLOCKLIST.has(normalizedHash);
  const forceTop = () => window.scrollTo(0, 0);

  if (shouldClearHash) {
    window.history.replaceState({}, '', `${pathname || '/'}${search || ''}`);
    // Re-apply top position across frames to beat browser anchor restoration timing
    forceTop();
    requestAnimationFrame(forceTop);
    setTimeout(forceTop, 0);
    setTimeout(forceTop, 120);
  }

  if (shouldClearHash || !normalizedHash) {
    forceTop();
  }
};

const StickyBar = ({ onCheckout }) => {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('stickyHidden') === '1');
  const [show, setShow] = useState(false);
  useEffect(() => {
    const updateVisibility = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const vh = window.innerHeight || 0;
      const dh = document.documentElement?.scrollHeight || 0;
      const stickyBar = document.querySelector('.sticky-bar');
      const stickyHeight = stickyBar?.getBoundingClientRect().height || 0;
      const contentHeight = Math.max(0, dh - stickyHeight);
      const distanceFromBottom = contentHeight - (scrollY + vh);
      // Use hysteresis so small scroll movements cannot rapidly toggle the bar.
      setShow((currentlyVisible) => {
        const threshold = currentlyVisible ? 320 : 100;
        const nextVisible = distanceFromBottom <= threshold;
        return currentlyVisible === nextVisible ? currentlyVisible : nextVisible;
      });
    };
    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateVisibility);
    const contentRoot = document.querySelector('main') || document.documentElement;
    resizeObserver?.observe(contentRoot);
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
      resizeObserver?.disconnect();
    };
  }, []);
  if (hidden || !show) return null;
  return (
    <div className="sticky-bar">
      <div className="sticky-inner">
        <div className="sticky-copy">
          <strong>Stop Checking for Your Visa Decision</strong> • Email Alerts + All 13 Premium Tools
        </div>
        <div className="sticky-actions">
          <button
            className="btn-primary"
            onClick={() => {
              if (typeof onCheckout === 'function') onCheckout();
              try {
                window.history.pushState({}, '', '/pricing');
                window.dispatchEvent(new PopStateEvent('popstate'));
              } catch (_) {
                try { window.location.href = '/pricing'; } catch (e) {}
              }
            }}
          >
            Upgrade for ₹349 – Alerts + 13 Tools
          </button>
          <button className="btn-dismiss" onClick={() => { sessionStorage.setItem('stickyHidden', '1'); setHidden(true); }}>×</button>
        </div>
      </div>
      </div>
    );
  };


function App() {
  const [route, setRoute] = useState(() => getRouteFromLocation());
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // Keep homepage steady at top and prevent jumps to hash sections (e.g., #preview)
  useLayoutEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }
        sanitizeHomeHashAndScrollTop();
        requestAnimationFrame(() => sanitizeHomeHashAndScrollTop());
        setTimeout(() => sanitizeHomeHashAndScrollTop(), 0);
      }
    } catch (e) {
      // no-op
    }
  }, []);
  const locationInfo = useMemo(() => {
    const [pathPart, queryPart] = (route || '').split('?');
    const info = {
      path: pathPart || '/',
      params: new URLSearchParams(queryPart || '')
    };
    return info;
  }, [route]);
  const currentPath = locationInfo.path;
  const queryParams = locationInfo.params;
  console.log('[DEBUG] currentPath:', currentPath);
  const [todayApproved, setTodayApproved] = useState(0);
  const [todayRefused, setTodayRefused] = useState(0);
  const [isToolsAccessLoading, setIsToolsAccessLoading] = useState(false);
  const [hasPremiumToolsAccess, setHasPremiumToolsAccess] = useState(false);
  const [premiumAccessState, setPremiumAccessState] = useState('checking');

  useEffect(() => {
    if (currentPath !== '/premium-access' && currentPath !== '/payment-success') {
      setPremiumAccessState('idle');
      return;
    }

    const canonicalOrigin = 'https://irelandvisatracker.in';
    if (currentPath === '/premium-access' && window.location.origin !== canonicalOrigin) {
      const query = queryParams.toString();
      window.location.replace(`${canonicalOrigin}/#/premium-access${query ? `?${query}` : ''}`);
      return;
    }

    const restoreAccess = async () => {
      const email = queryParams.get('email');
      const accessToken = queryParams.get('access_token');
      if (!email || !accessToken) {
        setPremiumAccessState('invalid');
        return;
      }

      const normalized = email.trim().toLowerCase();
      try {
        const response = await axios.get(`${API_BASE_URL}/api/subscription-status`, {
          params: { email: normalized },
          headers: { 'x-premium-access-token': accessToken }
        });
        const payload = response?.data || {};
        const paid = !!(payload.subscription?.paid ?? payload.paid);
        if (!paid) {
          setPremiumAccessState('invalid');
          return;
        }

        const sessionAccessToken = payload.access_token || accessToken;
        window.localStorage.setItem('userEmail', normalized);
        window.localStorage.setItem('premiumAccessToken', sessionAccessToken);
        window.sessionStorage.setItem('isPremiumUser', 'true');
        window.dispatchEvent(new Event('premium-access-changed'));
        setHasPremiumToolsAccess(true);
        setPremiumAccessState('valid');
      } catch (e) {
        console.error('[DEBUG] Premium access validation failed:', e);
        setPremiumAccessState('invalid');
      }
    };

    restoreAccess();
  }, [currentPath, queryParams]);

  useEffect(() => {
    if (currentPath === '/payment-failure') {
      try {
        window.sessionStorage.removeItem('ivtAnalyticsCheckoutPending');
      } catch (_) {
        // Ignore unavailable analytics storage.
      }
      trackAnalyticsEvent('payment_failure');
      return;
    }
    if (currentPath !== '/payment-success' || premiumAccessState !== 'valid' || !queryParams.get('txnid')) return;
    try {
      const transactionId = window.sessionStorage.getItem('ivtAnalyticsCheckoutPending');
      if (!transactionId) return;
      window.sessionStorage.removeItem('ivtAnalyticsCheckoutPending');
      trackAnalyticsEvent('purchase', {
        transaction_id: transactionId,
        currency: 'INR',
        value: 349,
        items: [{ item_id: 'premium-monitoring-tools', item_name: 'Premium monitoring and tools', price: 349, quantity: 1 }]
      });
    } catch (_) {
      // Analytics must never affect verified access.
    }
  }, [currentPath, premiumAccessState, queryParams]);

  useEffect(() => {
    const handleHomeHashChange = () => {
      sanitizeHomeHashAndScrollTop();
    };

    const handlePageShow = () => {
      sanitizeHomeHashAndScrollTop();
    };

    const onLocationChange = () => {
      sanitizeHomeHashAndScrollTop();
      setRoute(getRouteFromLocation());
    };
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('hashchange', handleHomeHashChange);
    window.addEventListener('hashchange', onLocationChange);
    window.addEventListener('popstate', onLocationChange);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('hashchange', handleHomeHashChange);
      window.removeEventListener('hashchange', onLocationChange);
      window.removeEventListener('popstate', onLocationChange);
    };
  }, []);

  useEffect(() => {
    if (currentPath !== '/') return;
    sanitizeHomeHashAndScrollTop();
    requestAnimationFrame(() => sanitizeHomeHashAndScrollTop());
  }, [currentPath]);

  useEffect(() => {
    let cancelled = false;

    const isToolsScope = currentPath === '/' || currentPath === '/tools' || currentPath === '/tools/' || currentPath.startsWith('/tools/');
    const checkToolsAccess = async () => {
      if (!isToolsScope) {
        if (!cancelled) {
          setIsToolsAccessLoading(false);
        }
        return;
      }

      let storedEmail = '';
      try {
        storedEmail = (window.localStorage.getItem('userEmail') || '').trim().toLowerCase();
      } catch (e) {
        storedEmail = '';
      }

      if (!storedEmail) {
        if (!cancelled) {
          setHasPremiumToolsAccess(false);
          setIsToolsAccessLoading(false);
        }
        return;
      }

      if (!cancelled) setIsToolsAccessLoading(true);
      try {
        const accessToken = window.localStorage.getItem('premiumAccessToken') || '';
        const resp = await axios.get(`${API_BASE_URL}/api/subscription-status`, {
          params: { email: storedEmail },
          headers: accessToken ? { 'x-premium-access-token': accessToken } : undefined
        });
        const payload = resp?.data || {};
        const paid = !!(payload.subscription?.paid ?? payload.paid);
        if (paid && payload.access_token) {
          window.localStorage.setItem('premiumAccessToken', payload.access_token);
          window.dispatchEvent(new Event('premium-access-changed'));
        }
        if (!cancelled) {
          setHasPremiumToolsAccess(paid);
          try {
            if (paid) window.sessionStorage.setItem('isPremiumUser', 'true');
          } catch (e) {
            // ignore storage errors
          }
        }
      } catch (e) {
        if (!cancelled) {
          setHasPremiumToolsAccess(false);
        }
      } finally {
        if (!cancelled) setIsToolsAccessLoading(false);
      }
    };

    checkToolsAccess();
    return () => {
      cancelled = true;
    };
  }, [currentPath]);

  // Load latest daily deltas for the top metrics (matches lastUpdated date)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [statsRes, deltasRes, trendsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/visa-stats`),
          axios.get(`${API_BASE_URL}/api/decision-deltas`).catch(() => ({ data: {} })),
          axios.get(`${API_BASE_URL}/api/daily-trends`).catch(() => ({ data: {} }))
        ]);

        const stats = statsRes?.data || {};
        const lastDate = (stats.lastUpdated || '').toString().split('T')[0];

        let a = null, r = null;

        // Prefer daily-trends.dailyDeltas for exact per-day deltas
        const dt = trendsRes?.data || {};
        if (Array.isArray(dt.dailyDeltas) && lastDate) {
          const match = dt.dailyDeltas.find(d => String(d.date) === String(lastDate));
          if (match) {
            a = Number(match.approvedDelta || 0);
            r = Number(match.refusedDelta || 0);
          }
        }

        // Fallback to decision-deltas series if not found
        if ((a === null || r === null) && deltasRes?.data) {
          const dd = deltasRes.data;
          const labels = Array.isArray(dd.labels) ? dd.labels : [];
          const aSeries = Array.isArray(dd.series) ? dd.series.find(s => s.name === 'Approved') : null;
          const rSeries = Array.isArray(dd.series) ? dd.series.find(s => s.name === 'Refused') : null;
          if (lastDate && labels.length && aSeries && rSeries) {
            const idx = labels.findIndex(l => String(l) === String(lastDate));
            if (idx >= 0) {
              a = Number((aSeries.data || [])[idx] || 0);
              r = Number((rSeries.data || [])[idx] || 0);
            }
          }
        }

        // If still nulls, choose the newest available between daily-trends and decision-deltas
        if (a === null || r === null) {
          const dtList = Array.isArray(dt.dailyDeltas) ? dt.dailyDeltas : [];
          if (dtList.length) {
            const last = dtList[dtList.length - 1];
            a = Number(last.approvedDelta || 0);
            r = Number(last.refusedDelta || 0);
          } else if (deltasRes?.data && Array.isArray(deltasRes.data.labels)) {
            const dd = deltasRes.data;
            const lastIdx = dd.labels.length - 1;
            const aSeries = Array.isArray(dd.series) ? dd.series.find(s => s.name === 'Approved') : null;
            const rSeries = Array.isArray(dd.series) ? dd.series.find(s => s.name === 'Refused') : null;
            a = Number((aSeries?.data || [])[lastIdx] || 0);
            r = Number((rSeries?.data || [])[lastIdx] || 0);
          }
        }

        if (!cancelled) {
          setTodayApproved(typeof a === 'number' && !isNaN(a) ? a : 0);
          setTodayRefused(typeof r === 'number' && !isNaN(r) ? r : 0);
        }
      } catch (e) {
        console.error('Failed to load top metrics', e);
        if (!cancelled) { setTodayApproved(0); setTodayRefused(0); }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const goHomeAndScroll = (id) => {
    try { window.history.pushState({}, '', '/'); } catch(_){}
    setRoute('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const openCheckout = () => setCheckoutOpen(true);

  const theme = useDocumentTheme();

  const toggleTheme = () => {
    setDocumentTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const themeToggleLabel = useMemo(() => (theme === 'dark' ? 'Light mode' : 'Dark mode'), [theme]);

  const HomeView = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [estimatorRef, estimatorVisible] = useScrollAnimation(0.1);
    const [calculatorRef, calculatorVisible] = useScrollAnimation(0.1);
    const [benefitsRef, benefitsVisible] = useScrollAnimation(0.1);
    const [pricingRef, pricingVisible] = useScrollAnimation(0.1);
    const [faqRef, faqVisible] = useScrollAnimation(0.1);
    const [showExitIntent, dismissExitIntent] = useExitIntent(50);
    
    return (
      <div className="App">
        <MetaTags
          title="Track Your Ireland Visa Decision - Official NDVO Monitoring"
          description="Monitor official NDVO visa decisions with email alerts, optional browser notifications and 13 premium planning tools. INR 349 one-time for Indian applicants."
          keywords="Ireland visa status India, check Ireland visa status, Ireland visa alerts, track Ireland visa from India, Ireland visa tracker, Ireland visa status, visa decision alerts, Ireland immigration, NDVO visa, AVATS Ireland visa status, VFS Ireland visa tracking, Ireland visa for Indians, visa processing time Ireland"
          ogImage="/images/og-image.webp"
        />
        <header className="site-header">
          <div className="site-header-inner">
            <a className="brand" href="/" onClick={() => setMenuOpen(false)}>
              <img src="/logo.svg" alt="Irish Visa Tracker logo" className="brand-mark" />
              <span>Track Your Ireland Visa Decision</span>
            </a>
            <a href="#main" className="skip-link">Skip to content</a>
            <button className="menu-toggle" aria-controls="primary-nav" aria-expanded={menuOpen} onClick={() => setMenuOpen(v => !v)}>
              <span className="sr-only">Toggle navigation</span>☰ Menu
            </button>
            <nav className={`nav ${menuOpen ? 'open' : ''}`} id="primary-nav" role="navigation" aria-label="Primary">
              <a href="/" onClick={() => setMenuOpen(false)}>Home</a>
              <a href="/resources" onClick={() => setMenuOpen(false)}>Resources</a>
              <a href="/tools" onClick={() => setMenuOpen(false)}>Tools</a>
              <a href="/pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
              <a href="/faq" onClick={() => setMenuOpen(false)}>FAQ</a>
              <a href="/about" onClick={() => setMenuOpen(false)}>About</a>
              <a href="/contact" onClick={() => setMenuOpen(false)}>Contact</a>
            </nav>
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              onMouseDown={(e) => e.preventDefault()}
            >
              {themeToggleLabel}
            </button>
          </div>
        </header>

      <GlobalBrowserAlertPrompt suppress={checkoutOpen} />
      <main id="main">
        {/* Hero */}
        <section className="hero" id="hero">
          <div className="hero-content">
            <h1>
              <span className="hero-highlight">Your Visa Decision Alert</span>
              <br />
              Sent to Your Inbox
            </h1>
            <p className="hero-subtext">
              Stop visiting the immigration website every day. We watch for your reference and email you when your decision is published.
              Get optional browser alerts and all <strong>13 premium planning tools</strong> for <strong>₹349 one-time.</strong> No subscription.
            </p>
            <div className="cta-group">
              <button className="btn-primary btn-shimmer" onClick={() => { openCheckout(); try { window.history.pushState({}, '', '/pricing'); } catch(_){} setRoute('/pricing'); }}>
                Unlock Alerts + 13 Tools for ₹349 →
              </button>
              <a href="/" className="btn-secondary" onClick={(e) => { e.preventDefault(); goHomeAndScroll('estimator'); }}>See Processing Times</a>
            </div>
            
            {/* Trust Signals */}
            <div className="hero-trust-signals">
              <div className="trust-item">
                <span className="trust-number"><CountUp end={todayApproved + todayRefused} /></span>
                <span className="trust-label">Decisions Tracked Today</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon">⚡</span>
                <span className="trust-label">Updated Daily</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon">🔒</span>
                <span className="trust-label">Secure & Private</span>
              </div>
            </div>
          </div>
        </section>

        {/* Processing Time Estimator - NEW */}
        <section ref={estimatorRef} className={`container section fade-section ${estimatorVisible ? 'fade-in' : ''}`} id="estimator">
          <ProcessingTimeEstimator onUpgrade={openCheckout} locked={!hasPremiumToolsAccess} hasPremiumAccess={hasPremiumToolsAccess} />
        </section>

        {/* Live Preview */}
        <section className="container section" id="preview">
          <h2>Live preview</h2>
          <div className="preview-grid">
            <div className="card">
              <h3>Search Visa Application</h3>
              <SearchBar />
            </div>
            <div className="card">
              <h3>Visa Decision Statistics</h3>
              <div className="mini-metrics">
                <div className="metric"><span className="muted">Latest Approvals</span><strong><CountUp end={todayApproved} /></strong></div>
                <div className="metric"><span className="muted">Latest Refusals</span><strong><CountUp end={todayRefused} /></strong></div>
              </div>
              <VisaChart />
            </div>
          </div>
        </section>

        {/* Statistics Trust Bar - NEW */}
        <StatisticsTrustBar todayApproved={todayApproved} todayRefused={todayRefused} />

        {/* How It Works - NEW */}
        <HowItWorks />

        {/* Decision Calculator - NEW */}
        <section ref={calculatorRef} className={`container section fade-section ${calculatorVisible ? 'fade-in' : ''}`} id="calculator">
          <DecisionCalculator onUpgrade={openCheckout} locked={!hasPremiumToolsAccess} hasPremiumAccess={hasPremiumToolsAccess} />
        </section>

        {/* Benefits */}
        <section ref={benefitsRef} className={`container section fade-section ${benefitsVisible ? 'fade-in' : ''}`} id="benefits">
          <h2>Why upgrade</h2>
          <div className="cards-grid">
            <div className="card animated-border benefit-featured">
              <span className="benefit-badge">Most Popular</span>
              <div className="benefit-icon">⚡</div>
              <h3>Decision Alerts Without Daily Checking</h3>
              <p>We watch the visa decision publications for your reference and send an email when it appears. Browser alerts are optional.</p>
            </div>
            <div className="card animated-border">
              <div className="benefit-icon">🧰</div>
              <h3>All 13 Premium Tools</h3>
              <p>Use every calculator and planning tool across the site with one upgrade.</p>
            </div>
            <div className="card animated-border">
              <div className="benefit-icon">📧</div>
              <h3>Email Notifications</h3>
              <p>Get the decision update at your registered email instead of repeatedly checking the page yourself.</p>
            </div>
            <div className="card animated-border">
              <div className="benefit-icon">🔒</div>
              <h3>Privacy-First</h3>
              <p>No spam. Only visa updates that matter.</p>
            </div>
            <div className="card animated-border">
              <div className="benefit-icon">🇮🇳</div>
              <h3>India Focus</h3>
              <p>Tailored for NDVO applicants in India.</p>
            </div>
            <div className="card animated-border">
              <div className="benefit-icon">💰</div>
              <h3>One-time ₹349</h3>
              <p>Simple upgrade. No recurring charges.</p>
            </div>
          </div>
        </section>

        {/* Enhanced Pricing Comparison - NEW */}
        <section ref={pricingRef} className={`fade-section ${pricingVisible ? 'fade-in' : ''}`} id="pricing">
          <PricingComparison onUpgrade={openCheckout} />
        </section>

        <section id="testimonials" aria-labelledby="testimonials-title">
          <EnhancedTestimonials />
        </section>

        {/* Spread the Word Section - NEW */}
        <SpreadTheWord />

        {/* SEO Content Section - NEW */}
        <SEOContent />

        {/* Enhanced FAQ - NEW */}
        <section ref={faqRef} className={`fade-section ${faqVisible ? 'fade-in' : ''}`} id="faq">
          <EnhancedFAQ />
        </section>

        {/* Support pages are available as dedicated routes now (see header/footer links) */}
      </main>

      <footer className="site-footer">
        <div className="container footer-inner">
          <div className="footer-disclaimer">
            <strong>Disclosure:</strong> This service is only for Indian citizens (From India) applying for Ireland Visas.
          </div>
          <div className="footer-top">
            <div className="brand">Track Your Ireland Visa Decision</div>
            <div className="links">
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="/refund">Refund</a>
              <a href="/data-deletion">Data Deletion</a>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="powered-by">
              Powered by <a href="https://selfdriven.careers" target="_blank" rel="noopener noreferrer">SelfDriven.careers</a>
            </div>
          </div>
        </div>
      </footer>

        <StickyBar onCheckout={openCheckout} />
        <SocialShareBar />
        <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
        <ExitIntentPopup 
          show={showExitIntent && !checkoutOpen && !hasPremiumToolsAccess && !isToolsAccessLoading}
          onClose={dismissExitIntent} 
          onCheckout={openCheckout} 
        />
        <CookieConsent />
      </div>
    );
  };

  const SimplePage = ({ title, children, fullWidth = false, article = false, metaTitle, metaDescription, metaKeywords, canonical, robots, breadcrumbs }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const isToolRoute = currentPath.startsWith('/tools/');
    const toolLabels = {
      '/tools/fee-calculator': 'Visa Fee Calculator',
      '/tools/document-checklist': 'Document Checklist Generator',
      '/tools/decision-calculator': 'Decision Calculator',
      '/tools/processing-time': 'Processing Time Estimator',
      '/tools/eligibility-quiz': 'Visa Eligibility Quiz',
      '/tools/education-loan-emi-calculator': 'Education Loan EMI Calculator',
      '/tools/cost-of-living-planner': 'Cost of Living Planner',
      '/tools/ireland-university-matcher': 'Ireland University Matcher',
      '/tools/ireland-scholarship-finder': 'Ireland Scholarship Finder',
      '/tools/ireland-visa-interview-simulator': 'Ireland Visa Interview Simulator',
      '/tools/ireland-stamp-visualizer': 'Ireland Stamp Transition Visualizer',
      '/tools/irish-salary-tax-calculator': 'Irish Salary Tax Calculator',
      '/tools/ireland-accommodation-assistant': 'Ireland Accommodation Assistant'
    };
    const pageBreadcrumbs = breadcrumbs || (isToolRoute
      ? [
        { label: 'Home', href: '/' },
        { label: 'Tools', href: '/tools' },
        { label: toolLabels[currentPath] || 'Premium Tool' }
      ]
      : undefined);
    return (
      <div className="App">
        <MetaTags
          title={metaTitle}
          description={metaDescription}
          keywords={metaKeywords}
          canonical={canonical}
          robots={robots}
        />
        <header className="site-header">
          <div className="site-header-inner">
            <a className="brand" href="/" onClick={() => setMenuOpen(false)}>
              <img src="/logo.svg" alt="Irish Visa Tracker logo" className="brand-mark" />
              <span>Track Your Ireland Visa Decision</span>
            </a>
            <a href="#main" className="skip-link">Skip to content</a>
            <button className="menu-toggle" aria-controls="primary-nav" aria-expanded={menuOpen} onClick={() => setMenuOpen(v => !v)}>
              <span className="sr-only">Toggle navigation</span>☰ Menu
            </button>
            <nav className={`nav ${menuOpen ? 'open' : ''}`} id="primary-nav" role="navigation" aria-label="Primary">
              <a href="/" onClick={() => setMenuOpen(false)}>Home</a>
              <a href="/resources" onClick={() => setMenuOpen(false)}>Resources</a>
              <a href="/tools" onClick={() => setMenuOpen(false)}>Tools</a>
              <a href="/pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
              <a href="/faq" onClick={() => setMenuOpen(false)}>FAQ</a>
              <a href="/about" onClick={() => setMenuOpen(false)}>About</a>
              <a href="/contact" onClick={() => setMenuOpen(false)}>Contact</a>
              <button className="btn-primary" onClick={openCheckout}>Upgrade for ₹349 – Alerts + 13 Tools</button>
            </nav>
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              onMouseDown={(e) => e.preventDefault()}
            >
              {themeToggleLabel}
            </button>
          </div>
        </header>
      <GlobalBrowserAlertPrompt suppress={checkoutOpen} />
      <main id="main" {...(article ? { 'data-article-content': true } : {})}>
        <section className={`container section${fullWidth ? ' section--full' : ''}`}>
          <Breadcrumbs items={pageBreadcrumbs} />
          {title && <h2 className="simple-page-title">{title}</h2>}
          <div className={fullWidth ? '' : 'card'}>
            {children}
          </div>
        </section>
      </main>
      <footer className="site-footer">
        <div className="container footer-inner">
          <div className="footer-top">
            <div className="brand">Track Your Ireland Visa Decision</div>
            <div className="links">
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="/refund">Refund</a>
              <a href="/data-deletion">Data Deletion</a>
              <a href="/editorial-policy">Editorial Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="powered-by">
              Powered by <a href="https://selfdriven.careers" target="_blank" rel="noopener noreferrer">SelfDriven.careers</a>
            </div>
          </div>
        </div>
      </footer>
      {!isToolRoute && <StickyBar onCheckout={openCheckout} />}
      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </div>
  );
  };

  const ResourcesLanding = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const filteredArticles = useMemo(() => {
      return articles.filter(article => {
        const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
        
        const query = searchQuery.toLowerCase().trim();
        if (!query) return matchesCategory;

        const titleMatch = article.title?.toLowerCase().includes(query);
        const descMatch = article.description?.toLowerCase().includes(query);
        const keywordsMatch = article.keywords?.some(kw => kw.toLowerCase().includes(query));

        return matchesCategory && (titleMatch || descMatch || keywordsMatch);
      });
    }, [searchQuery, selectedCategory]);

    const groupedArticles = useMemo(() => {
      return filteredArticles.reduce((acc, article) => {
        acc[article.category] = acc[article.category] || [];
        acc[article.category].push(article);
        return acc;
      }, {});
    }, [filteredArticles]);

    return (
      <SimplePage 
        title="Ireland Visa Resources" 
        fullWidth
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Resources' }]}
        metaTitle="Ireland Visa Resources - Complete Guides for Indian Applicants"
        metaDescription="Comprehensive Ireland visa guides for Indian students, professionals, and families. Step-by-step visa process, document checklists, processing times, and success tips."
        metaKeywords="Ireland visa guides, Irish visa resources, Ireland visa for Indians, visa application process Ireland, Ireland visa documents, visa processing time Ireland"
      >
        <header className="resources-header">
          <h1>Ireland Visa Resources for Indian Applicants</h1>
          <p className="resources-lede">
            Deep-dive guides for Indian students, professionals, and families
            navigating the Irish visa journey. Search or choose a category
            below to explore articles.
          </p>
        </header>
        <div className="resources-intro">
          <p>
            Search or choose a category below to explore articles.
          </p>
        </div>

        <div className="resources-controls">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              placeholder="Search articles (e.g. visa, bank, checklist)..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="resources-search-input"
            />
          </div>
          <div className="filter-select-wrapper">
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="resources-filter-select"
            >
              <option value="all">All Categories</option>
              {Object.entries(categoryInfo).map(([key, info]) => (
                <option key={key} value={key}>{info.name}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredArticles.length === 0 ? (
          <div className="resources-no-results">
            <h3>No results found</h3>
            <p>We couldn't find any articles matching "{searchQuery}". Try adjusting your keywords or category.</p>
          </div>
        ) : (
          <div className="resources-categories">
            {Object.entries(groupedArticles).map(([category, list]) => {
              const catInfo = categoryInfo[category] || { name: category, description: '', color: '#10b981' };
              return (
                <section key={category} className="resources-category-section">
                  <div className="category-header" style={{ borderLeftColor: catInfo.color }}>
                    <h3 className="category-title">{catInfo.name}</h3>
                    <p className="category-description">{catInfo.description}</p>
                  </div>
                  <div className="articles-grid">
                    {list.map((article) => (
                      <a 
                        key={article.slug} 
                        href={`/resources/${article.slug}`} 
                        className={`article-card ${article.pillar ? 'article-card--pillar' : ''}`}
                      >
                        <div className="article-card__icon" style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color }}>
                          {article.icon || '📝'}
                        </div>
                        <div className="article-card__content">
                          <h4 className="article-card__title">{article.title}</h4>
                          <p className="article-card__description">{article.description}</p>
                          <div className="article-card__meta">
                            <span className="article-card__read-time">📖 {article.readTime} min read</span>
                            {article.pillar && <span className="article-card__badge">Complete Guide</span>}
                          </div>
                        </div>
                        <div className="article-card__arrow">→</div>
                      </a>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </SimplePage>
    );
  };

  const ArticleView = ({ slug }) => {
    // Validate slug up front so an unknown article never renders an
    // indexable shell. A slug with strange characters is also rejected.
    const isValidSlug = typeof slug === 'string' && /^[a-z0-9-]+$/.test(slug);
    const meta = isValidSlug ? articles.find((article) => article.slug === slug) : null;
    const articleExists = isValidSlug && Boolean(meta);
    const [state, setState] = useState({ loading: true, error: null, frontmatter: {}, content: '', Component: null });

    useEffect(() => {
      if (!articleExists) {
        setState({ loading: false, error: new Error('Article not found'), frontmatter: {}, content: '', Component: null });
        return undefined;
      }
      let cancelled = false;
      const load = async () => {
        setState((prev) => ({ ...prev, loading: true, error: null, Component: null }));
        try {
          const response = await fetch(`/articles/${slug}.md`);
          if (!response.ok) throw new Error('Article not found');
          const text = await response.text();
          const parsed = matter(text);
          
          // Evaluate MDX with proper runtime and components
          const evaluated = await evaluate(parsed.content || '', {
            ...runtime,
            Fragment,
            useMDXComponents: () => mdxComponents,
            development: false
          });
          
          const ContentComponent = evaluated.default;
          if (!cancelled) {
            setState({
              loading: false,
              error: null,
              frontmatter: parsed.data || {},
              content: parsed.content || '',
              Component: ContentComponent || null
            });
          }
        } catch (err) {
          console.error('MDX evaluation error:', err);
          if (!cancelled) {
            setState({ loading: false, error: err, frontmatter: {}, content: '', Component: null });
          }
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [slug, articleExists]);

    if (!articleExists) {
      return (
        <SimplePage
          title="Page not found"
          fullWidth
          metaTitle="Page Not Found - Irish Visa Tracker"
          metaDescription="The page you are looking for does not exist or has moved."
          robots="noindex, follow"
        >
          <NotFound />
        </SimplePage>
      );
    }

    const title = state.frontmatter?.title || meta?.title || 'Resources';
    const metaInfo = [
      state.frontmatter?.publishDate && `Published ${state.frontmatter.publishDate}`,
      state.frontmatter?.lastUpdated && `Updated ${state.frontmatter.lastUpdated}`,
      state.frontmatter?.readingTime && `${state.frontmatter.readingTime} min read`
    ].filter(Boolean);

    const articleSchema = {
      schemaType: state.frontmatter?.schema || 'Article',
      title,
      description:
        state.frontmatter?.metaDescription ||
        `Learn about ${title.toLowerCase()} for Ireland visa applicants.`,
      canonical: state.frontmatter?.canonicalUrl || `/resources/${slug}`,
      author: state.frontmatter?.author,
      datePublished: state.frontmatter?.publishDate,
      dateModified: state.frontmatter?.lastUpdated,
      image: state.frontmatter?.featuredImage?.src,
      keywords: [
        state.frontmatter?.primaryKeyword,
        ...(state.frontmatter?.secondaryKeywords || [])
      ].filter(Boolean)
    };

    return (
      <SimplePage
        fullWidth
        article
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Resources', href: '/resources' },
          { label: title }
        ]}
      >
        <MetaTags
          title={state.frontmatter?.metaTitle || `${title} - Irish Visa Tracker`}
          description={state.frontmatter?.metaDescription || `Learn about ${title.toLowerCase()} for Ireland visa applicants. Comprehensive guide with tips and requirements.`}
          keywords={state.frontmatter?.primaryKeyword ? `${state.frontmatter.primaryKeyword}, ${state.frontmatter.secondaryKeywords?.join(', ') || ''}, Ireland visa guide` : `Ireland visa, ${title.toLowerCase()}, visa guide`}
          canonical={state.frontmatter?.canonicalUrl || `/resources/${slug}`}
          ogImage={state.frontmatter?.featuredImage?.src}
        />
        <ArticleSchema {...articleSchema} />
        <article className="article">
          {state.loading && <p>Loading article…</p>}
          {!state.loading && state.error && (
            <p>Unable to load this article right now.</p>
          )}
          {!state.loading && !state.error && state.Component && (
            <>
              <Byline
                author={state.frontmatter?.author}
                datePublished={state.frontmatter?.publishDate}
                dateModified={state.frontmatter?.lastUpdated}
                readingTime={state.frontmatter?.readingTime}
              />
              {metaInfo.length > 0 && (
                <div className="article-meta">
                  {metaInfo.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              )}
              <div className="article-content">
                <MDXProvider components={mdxComponents}>
                  <state.Component components={mdxComponents} />
                </MDXProvider>
              </div>
            </>
          )}
          {!state.loading && !state.error && state.Component && (
            <div className="article-cta">
              <div className="article-cta-card">
                <h3>📬 Get notified when your visa decision is published</h3>
                <p>
                  Stop checking the NDVO website every day. Upgrade once for{' '}
                  <strong>₹349</strong> and receive instant email + web push
                  alerts the moment your Ireland visa status changes.
                </p>
                <div className="article-cta-actions">
                  <button className="btn-primary" onClick={openCheckout}>
                    Upgrade for ₹349 – Alerts + 13 Tools
                  </button>
                  <a href="/pricing" className="btn-secondary">
                    See what's included
                  </a>
                </div>
              </div>
            </div>
          )}
        </article>
      </SimplePage>
    );
  };

  if ((currentPath === '/tools' || currentPath === '/tools/') && isToolsAccessLoading) {
    return (
      <SimplePage
        title="Checking access"
        fullWidth
        metaTitle="Checking Premium Access - Irish Visa Tracker"
        metaDescription="Verifying your premium tools access."
        robots="noindex, nofollow"
      >
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <h2>Checking premium access...</h2>
          <p>Please wait while we verify your subscription.</p>
        </div>
      </SimplePage>
    );
  }
  if (currentPath === '/tools' || currentPath === '/tools/') {
    console.log('[DEBUG] Matched /tools route');
    return (
      <>
        <ToolsPage onUpgrade={openCheckout} hasPremiumAccess={hasPremiumToolsAccess} />
        <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
      </>
    );
  }
  if (currentPath.startsWith('/tools/') && isToolsAccessLoading) {
    return (
      <SimplePage
        title="Checking access"
        fullWidth
        metaTitle="Checking Premium Access - Irish Visa Tracker"
        metaDescription="Verifying your premium tools access."
        robots="noindex, nofollow"
      >
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <h2>Checking premium access...</h2>
          <p>Please wait while we verify your subscription.</p>
        </div>
      </SimplePage>
    );
  }
  if (currentPath.startsWith('/tools/') && !hasPremiumToolsAccess) {
    return (
      <SimplePage
        title="Premium Tool"
        fullWidth
        metaTitle="Premium Visa Tool - Upgrade Required"
        metaDescription="This tool is available for premium users. Upgrade once to unlock instant alerts and all 13 premium Ireland visa tools."
        robots="noindex, follow"
      >
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <h2>🔒 This tool is for premium users</h2>
          <p>Upgrade once for ₹349 to unlock all 13 tools, calculators, and instant alerts.</p>
          <button className="btn-primary btn-shimmer" onClick={openCheckout}>
            Upgrade for ₹349 – Alerts + 13 Tools
          </button>
        </div>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/fee-calculator') {
    console.log('[DEBUG] Matched /tools/fee-calculator route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Ireland Visa Fee Calculator - Calculate Visa Costs in INR"
        metaDescription="Estimate Ireland entry-visa and optional application costs in INR. Confirm current immigration, VFS, courier, and insurance charges before payment."
        metaKeywords="Ireland visa fee calculator, Ireland visa cost, visa fees India, VFS Ireland charges, Ireland visa price"
      >
        <Suspense fallback={<ToolFallback />}>
          <LazyFeeCalculator />
        </Suspense>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/document-checklist') {
    console.log('[DEBUG] Matched /tools/document-checklist route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Ireland Visa Document Checklist Generator - Required Documents"
        metaDescription="Generate a customized document checklist for your Ireland visa application. Check off documents as you collect them and download your personalized list."
        metaKeywords="Ireland visa documents, visa checklist, Ireland visa requirements, document checklist Ireland, visa application documents"
      >
        <Suspense fallback={<ToolFallback />}>
          <LazyDocumentChecklist />
        </Suspense>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/decision-calculator') {
    console.log('[DEBUG] Matched /tools/decision-calculator route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Visa Decision Calculator - Application Readiness Check"
        metaDescription="Review Ireland visa application readiness based on visa type, documents, application date, and refusal history. This educational tool is not an approval prediction."
        metaKeywords="Ireland visa application readiness, visa document checklist, Ireland visa planning tool"
      >
        <DecisionCalculator onUpgrade={openCheckout} hasPremiumAccess={hasPremiumToolsAccess} />
      </SimplePage>
    );
  }
  if (currentPath === '/tools/processing-time') {
    console.log('[DEBUG] Matched /tools/processing-time route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Processing Time Estimator - Ireland Visa Processing Times"
        metaDescription="Estimate an indicative Ireland visa processing range based on visa type and historical category averages. This is not an official deadline or decision prediction."
        metaKeywords="Ireland visa processing time, visa timeline estimator, visa processing calculator, Ireland immigration timeline"
      >
        <div className="container section">
          <ProcessingTimeEstimator onUpgrade={openCheckout} hasPremiumAccess={hasPremiumToolsAccess} />
        </div>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/eligibility-quiz') {
    console.log('[DEBUG] Matched /tools/eligibility-quiz route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Ireland Visa Eligibility Quiz - Check Your Eligibility"
        metaDescription="Take our interactive quiz to check your eligibility for an Ireland visa. Get personalized recommendations for visa types and next steps."
        metaKeywords="Ireland visa eligibility, visa eligibility quiz, Ireland visa requirements, check visa eligibility, Ireland visa for Indians"
      >
        <Suspense fallback={<ToolFallback />}>
          <LazyEligibilityQuiz onUpgrade={openCheckout} hasPremiumAccess={hasPremiumToolsAccess} />
        </Suspense>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/education-loan-emi-calculator') {
    console.log('[DEBUG] Matched /tools/education-loan-emi-calculator route');
    return (
      <SimplePage
        title=""
        fullWidth
        metaTitle="Education Loan EMI Calculator - Ireland Student Visa"
        metaDescription="Calculate your education loan EMI for Ireland studies. Compare bank interest rates, view amortization schedules, and plan pre-payments."
        metaKeywords="education loan EMI calculator, Ireland student loan, SBI education loan, HDFC Credila, Axis Bank education loan, loan EMI calculator"
      >
        <Suspense fallback={<ToolFallback />}>
          <LazyEducationLoan />
        </Suspense>
      </SimplePage>
    );
  }
  if (currentPath === '/tools/cost-of-living-planner') return (
    <SimplePage 
      title=""
      fullWidth
      metaTitle="Cost of Living Planner - Ireland Student Budget"
      metaDescription="Plan your monthly budget for studying in Ireland. Compare costs across Dublin, Cork, Galway, and Limerick with lifestyle presets and money-saving tips."
      metaKeywords="Ireland cost of living, student budget Ireland, Dublin living cost, Cork accommodation, Ireland expenses calculator, student budget planner"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyCostOfLiving />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/ireland-university-matcher') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Ireland University Matcher - Find Your Perfect University"
      metaDescription="Find the perfect Irish university based on your academic profile, course preferences, and budget. Compare 7 Irish universities with smart matching algorithm."
      metaKeywords="Ireland university matcher, Irish universities, TCD, UCD, DCU, UL, NUIG, UCC, TU Dublin, university selection Ireland"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyUniversityMatcher />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/ireland-scholarship-finder') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Ireland Scholarship Finder - Find Scholarships for Indian Students"
      metaDescription="Discover scholarships available for Indian students to study in Ireland. Find government, university, and organization scholarships with eligibility matching."
      metaKeywords="Ireland scholarships, Indian students Ireland, Ireland scholarship finder, study in Ireland scholarship, Irish university scholarships"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyScholarshipFinder />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/ireland-visa-interview-simulator') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Ireland Visa Interview Simulator - Practice Interview Questions"
      metaDescription="Practice Ireland visa interview with 50+ questions based on visa type. Get sample answers, red flags to avoid, and scoring for Student, Work, Tourist visas."
      metaKeywords="Ireland visa interview, visa interview questions, Ireland student visa interview, visa interview practice, Ireland visa preparation"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyInterviewSimulator />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/ireland-stamp-visualizer') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Ireland Stamp Transition Visualizer - Visa Pathway Guide"
      metaDescription="Interactive flowchart showing Ireland visa stamp progression from Student (Stamp 2) to Citizenship. Calculate your path to PR with timeline and requirements."
      metaKeywords="Ireland stamp types, Stamp 2 Ireland, Stamp 4 Ireland, Ireland PR path, Ireland citizenship, Irish visa progression, Stamp 1G graduate scheme"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyStampVisualizer />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/irish-salary-tax-calculator') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Irish Salary Tax Calculator - Take-Home Pay After Taxes"
      metaDescription="Calculate your take-home salary after Irish taxes (PAYE, USC, PRSI). Check Critical Skills permit eligibility and compare with Indian take-home pay."
      metaKeywords="Irish tax calculator, Ireland salary calculator, PAYE USC PRSI, Irish take-home pay, Critical Skills permit salary, Ireland work permit salary"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyTaxCalculator />
      </Suspense>
    </SimplePage>
  );
  if (currentPath === '/tools/ireland-accommodation-assistant') return (
    <SimplePage
      title=""
      fullWidth
      metaTitle="Ireland Accommodation Assistant - Find Student Housing"
      metaDescription="Find student accommodation in Ireland with city guides, platform comparisons, price estimates, scam protection tips, and document checklists."
      metaKeywords="Ireland student accommodation, Dublin student housing, Cork accommodation, Galway housing, Ireland rent guide, student accommodation Ireland"
    >
      <Suspense fallback={<ToolFallback />}>
        <LazyAccommodation />
      </Suspense>
    </SimplePage>
  );
  if (currentPath.startsWith('/resources/')) {
    const slug = currentPath.replace('/resources/', '').replace(/\/$/, '');
    if (!slug) return <ResourcesLanding />;
    return <ArticleView slug={slug} />;
  }
  if (currentPath === '/pricing') return (
    <SimplePage
      fullWidth
      metaTitle="Pricing - Irish Visa Tracker ₹349 One-Time"
      metaDescription="Get decision email alerts, optional browser notifications, unlimited searches and all 13 premium tools for ₹349 one-time. No subscription."
      metaKeywords="Ireland visa tracker pricing, visa alerts price, Ireland visa notifications, visa tracker upgrade"
    >
      <PricingPage onUpgrade={openCheckout} />
    </SimplePage>
  );
  if (currentPath === '/resources') return <ResourcesLanding />;
  if (currentPath === '/faq') return (
    <SimplePage
      fullWidth
      metaTitle="Ireland Visa FAQ - Common Questions & Answers"
      metaDescription="Frequently asked questions about Irish Visa Tracker, decision alerts, data sources, pricing, refunds and premium access."
      metaKeywords="Ireland visa FAQ, Irish visa questions, visa tracker FAQ, Ireland visa alerts, visa status updates"
    >
      <FaqPage />
    </SimplePage>
  );
  if (currentPath === '/about') return (
    <SimplePage 
      title=""
      fullWidth
      metaTitle="About Irish Visa Tracker - Track Ireland Visa Decisions"
      metaDescription="Learn about Irish Visa Tracker - a service that monitors daily Irish visa decisions and provides instant alerts to Indian applicants. Official data, real-time updates."
      metaKeywords="about Irish visa tracker, Ireland visa monitoring, visa decision tracking, Irish immigration data"
    >
      <AboutPage />
    </SimplePage>
  );
  if (currentPath === '/contact') return (
    <SimplePage 
      title=""
      fullWidth
      metaTitle="Contact Irish Visa Tracker - Get in Touch"
      metaDescription="Contact Irish Visa Tracker for questions, feedback, or support. Reach out via email for visa tracking service inquiries and assistance."
      metaKeywords="contact Irish visa tracker, visa tracker support, Ireland visa help, visa guide contact"
    >
      <ContactPage />
    </SimplePage>
  );
  if (currentPath === '/privacy') return (
    <SimplePage
      fullWidth
      metaTitle="Privacy Policy - Irish Visa Tracker"
      metaDescription="Irish Visa Tracker privacy policy. Learn how we protect your data, what information we collect, and our commitment to privacy and security."
      metaKeywords="privacy policy, data protection, visa tracker privacy, user data security"
    >
      <PrivacyPage />
    </SimplePage>
  );
  if (currentPath === '/terms') return (
    <SimplePage
      fullWidth
      metaTitle="Terms of Service - Irish Visa Tracker"
      metaDescription="Irish Visa Tracker terms of service. Learn about our service usage terms, data disclaimer, and user responsibilities."
      metaKeywords="terms of service, user agreement, visa tracker terms, service conditions"
    >
      <TermsPage />
    </SimplePage>
  );
  if (currentPath === '/refund') return (
    <SimplePage
      fullWidth
      metaTitle="Refund Policy - Irish Visa Tracker"
      metaDescription="Understand Irish Visa Tracker's non-refundable one-time premium purchase, why the fee covers service setup and access, and how to get help with billing or service issues."
      metaKeywords="refund policy, non-refundable premium, one-time service, billing support"
    >
      <RefundPage />
    </SimplePage>
  );
  if (currentPath === '/data-deletion') return (
    <SimplePage
      fullWidth
      metaTitle="Data Deletion Request - Irish Visa Tracker"
      metaDescription="Learn how to request deletion of your data from Irish Visa Tracker. Follow our process to remove your personal information and visa tracking data."
      metaKeywords="data deletion, GDPR compliance, privacy rights, data removal, visa tracker data"
    >
      <DataDeletionPage />
    </SimplePage>
  );
  if (currentPath === '/editorial-policy') return (
    <SimplePage
      fullWidth
      metaTitle="Editorial Policy - Irish Visa Tracker"
      metaDescription="How Irish Visa Tracker creates, reviews, and updates its visa guides. Sources, authors, and review cadence."
      metaKeywords="editorial policy, content standards, fact checking, visa tracker, immigration content"
    >
      <EditorialPolicyPage />
    </SimplePage>
  );
  if (currentPath === '/payment-success') {
    const txnid = queryParams.get('txnid');
    const refsRaw = queryParams.get('refs') || '';
    const refs = refsRaw.split(',').map((item) => item.trim()).filter(Boolean);
    return (
      <SimplePage
        fullWidth
        metaTitle="Payment Successful - Irish Visa Tracker"
        metaDescription="Your payment was successful! Welcome to Irish Visa Tracker premium service with instant alerts and all 13 premium tools."
        metaKeywords="payment successful, visa tracker upgrade, premium service, visa alerts activated"
        robots="noindex, nofollow"
      >
        <p>Thank you! Your upgrade is confirmed. We'll start monitoring your visa reference numbers, send instant alerts, and unlock all 13 premium tools.</p>
        {txnid && <p><strong>Transaction ID:</strong> {txnid}</p>}
        {refs.length > 0 && (
          <div>
            <strong>Reference numbers:</strong>
            <ul>
              {refs.map((ref) => (
                <li key={ref}>{ref}</li>
              ))}
            </ul>
          </div>
        )}
        <div style={{marginTop: '1.5rem', padding: '1rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px'}}>
          <p style={{margin: 0, fontWeight: 500}}>📧 Check your email for login credentials and welcome instructions.</p>
          <p style={{margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.85}}>We have automatically activated your premium access on this browser. You do not need to sign in manually.</p>
        </div>
        <div style={{marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
          <a href="/" className="btn-primary" style={{textDecoration: 'none', display: 'inline-block', padding: '0.6rem 1.2rem', borderRadius: '6px'}}>
            Go to Search Dashboard
          </a>
          <a href="/tools" className="btn-secondary" style={{textDecoration: 'none', display: 'inline-block', padding: '0.6rem 1.2rem', borderRadius: '6px'}}>
            Explore Premium Tools
          </a>
        </div>
        <p style={{marginTop: '1.5rem'}}>If you don't receive a confirmation email within a few minutes, contact <a href="mailto:visahelp@irelandvisatracker.in">visahelp@irelandvisatracker.in</a>.</p>
      </SimplePage>
    );
  }
  if (currentPath === '/premium-access') {
    if (queryParams.get('reason') === 'activation_pending') {
      return (
        <SimplePage fullWidth metaTitle="Premium Activation In Progress - Irish Visa Tracker"
          metaDescription="Payment confirmation received. Recover your paid access after activation completes."
          robots="noindex, nofollow">
          <h1>Premium activation is in progress</h1>
          <p>Your payment confirmation was received. Please do not pay again.</p>
          <p>Allow a moment for activation, then use "Already paid? Restore access" on the dashboard with your purchase email.</p>
          <a href="/" className="btn-primary">Go to access recovery</a>
          <p>If access is still unavailable, contact <a href="mailto:visahelp@irelandvisatracker.in">visahelp@irelandvisatracker.in</a> with your payment receipt.</p>
        </SimplePage>
      );
    }
    return (
      <SimplePage
        fullWidth
        metaTitle="Premium Access Restored - Irish Visa Tracker"
        metaDescription="Your secure premium access link has been accepted."
        robots="noindex, nofollow"
      >
        {premiumAccessState === 'valid' ? (
          <>
            <h1>Premium access restored</h1>
            <p>This browser is now authorized for your paid Irish Visa Tracker account.</p>
            <a href="/" className="btn-primary">Continue to dashboard</a>
          </>
        ) : premiumAccessState === 'invalid' ? (
          <>
            <h1>Access link could not be verified</h1>
            <p>This link may be expired, incomplete, or tied to a different account. Request a new recovery link from the dashboard.</p>
            <a href="/" className="btn-primary">Return to dashboard</a>
          </>
        ) : (
          <>
            <h1>Verifying premium access...</h1>
            <p>Please wait while we confirm your paid account.</p>
          </>
        )}
      </SimplePage>
    );
  }
  if (currentPath === '/payment-failure') {
    const reason = queryParams.get('reason');
    return (
      <SimplePage
        fullWidth
        metaTitle="Payment Failed - Irish Visa Tracker"
        metaDescription="Your payment could not be processed. No charges were made. Please try again or contact support for assistance with your Irish Visa Tracker upgrade."
        metaKeywords="payment failed, payment error, visa tracker payment, transaction failed"
        robots="noindex, nofollow"
      >
        <p>We couldn't complete your payment. No charges were made.</p>
        {reason && <p><strong>Details:</strong> {reason.replace(/_/g, ' ')}</p>}
        <p>Please try again or reach out to <a href="mailto:visahelp@irelandvisatracker.in">visahelp@irelandvisatracker.in</a> for help.</p>
        <button className="btn-primary" onClick={openCheckout}>Retry payment</button>
      </SimplePage>
    );
  }

  console.log('[DEBUG] No route matched, falling through to HomeView');
  return (
    <>
      <HomeView />
      <ActivityFeed suppress={hasPremiumToolsAccess || isToolsAccessLoading} />
    </>
  );
}

export default App;
